/**
 * App shell - main layout with plugin slots
 */

import React, { ReactNode, useState, useEffect, useMemo } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ToastContainer } from '../UI/Toast';
import { MockRoleBanner } from '../RBAC/MockRoleBanner';
import { NavItem } from '../../types/plugins';
import { getBaseUrl } from '../../utils/env';

import { TraceKitConfig } from '../../types/config';

export interface AppShellProps {
  children: ReactNode;
  pluginSidebarItems?: NavItem[];
  pluginHeaderActions?: React.ComponentType<any>[];
  onNavigate?: (path: string) => void;
  currentPath?: string;
  defaultTenantId?: string;
  defaultProjectId?: string;
  /** When false, tenant/project dropdowns are hidden and workspace is synced to config defaults (non–tech-admin users). */
  showWorkspaceSelectors?: boolean;
  config?: TraceKitConfig;
}

export function AppShell({
  children,
  pluginSidebarItems,
  pluginHeaderActions,
  onNavigate,
  currentPath,
  defaultTenantId,
  defaultProjectId,
  showWorkspaceSelectors,
  config,
}: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Expand sidebar when on home page, collapse on other pages
  useEffect(() => {
    if (currentPath === '/') {
      setSidebarCollapsed(false);
    } else {
      setSidebarCollapsed(true);
    }
  }, [currentPath]);

  // Collapse sidebar when clicking on main content area (but not on interactive elements)
  const handleMainClick = (e: React.MouseEvent<HTMLElement>) => {
    // Don't collapse if clicking on interactive elements
    const target = e.target as HTMLElement;
    const isInteractive = target.closest('button, a, input, select, textarea, [role="button"], [onclick]');
    
    if (!isInteractive && currentPath !== '/' && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  };

  // Resolve logo URL with base path so it works when app is served at e.g. /keystone-rfp/
  const logoUrl = useMemo(() => {
    const raw = config?.branding?.logoUrl || config?.branding?.logo;
    if (!raw || raw.startsWith('http')) return raw;
    const base = getBaseUrl();
    const basePath = base === '/' ? '' : base.replace(/\/$/, '');
    return `${basePath}${raw}`;
  }, [config?.branding?.logoUrl, config?.branding?.logo]);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        pluginItems={pluginSidebarItems}
        onNavigate={onNavigate}
        currentPath={currentPath}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        logo={logoUrl}
        logoAlt={config?.branding?.logoAlt || config?.branding?.appName}
        platformTitle={config?.branding?.platformTitle || config?.branding?.appName}
        platformSubtitle={config?.branding?.platformSubtitle}
      />
      
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header 
          pluginActions={pluginHeaderActions}
          defaultTenantId={defaultTenantId}
          defaultProjectId={defaultProjectId}
          showWorkspaceSelectors={showWorkspaceSelectors}
          onNavigate={onNavigate}
        />
        
        <MockRoleBanner />
        
        <main 
          className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto p-6"
          style={{
            backgroundColor: 'var(--color-background-default, rgb(249, 250, 251))'
          }}
          onClick={handleMainClick}
        >
          {children}
        </main>
      </div>
      
      <ToastContainer />
    </div>
  );
}

